import React, { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import PageContainer from "../../../components/PageContainer";
import DataTable from "../../../components/DataTable";
import Pagination from "../../../components/Pagination";
import EmptyState from "../../../components/EmptyState";
import TableSkeleton from "../../../components/TableSkeleton";
import TableToolbar from "../../../components/TableToolbar";
import SearchInput from "../../../components/filters/SearchInput";
import useOrdersList from "../../../hooks/useOrdersList";
import usePartners from "../../hooks/usePartners";
import useUpdateOrder from "../../../hooks/useUpdateOrder";
import { isRequestError } from "../../../hooks/request-error";
import { request } from "../../../../config/helpers/axios-instance";
import { DownLoadTable } from "../../../../utils/DownloadtablesInExcel";
import { getOrderStatuses } from "../../../../utils/helpers";
import { buildB2BColumns, buildB2BExport, UNAVAILABLE_COLUMNS } from "./columns";
import { DEFAULT_PAGE_SIZE } from "../../../constants/pagination";

// B2B / Partner orders.
//
// Chips come from the live partners table, not the prototype's five hardcoded vendor
// names — three of those five are wrong or empty against real data (Nutrapack has no
// partner row and zero orders; GLP1 has no partner row; only Visit and ACKO are
// genuinely channel=B2B). Fourteen real partners have orders, so a fixed list of five
// would hide the second-highest-volume partner entirely.
//
// The cancel-comment dropdowns persist, unlike the prototype's (which are uncontrolled
// and write nowhere). They save into `cancelledReason` via PUT /userOrder/admin/id/:id.
// The payload carries ONLY that field: the endpoint warns loudly on partnerId /
// partnerEntityId changes, and a comment save must never look like a partner
// reassignment. The Order Status select writes through the same hook and obeys the same
// rule: `{ status }` and nothing else.
//
// This page carries the legacy screens' row affordances: an inline status select, an
// Action column (edit / delete), row selection with select-all, and an Excel export.
// They are the shared components in src/v2/components/, so Orders and B2B stay identical.

const PARAM = { partner: "partner", q: "q", page: "page" };

/** Placeholder used before the record count is known — the page's original text. */
const SEARCH_PLACEHOLDER = "User or caregiver name";

function B2BPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const partnersQuery = usePartners();
    const updateOrder = useUpdateOrder();
    const [savingId, setSavingId] = useState(null);
    // Names the field of the write in flight (or the one that just failed), because two
    // different columns now share `updateOrder` and an error banner that says "cancel
    // comment" after a failed status change would be a lie.
    const [savingLabel, setSavingLabel] = useState("change");
    const [deleteError, setDeleteError] = useState(null);
    const [selectedIds, setSelectedIds] = useState(() => new Set());

    // getOrderStatuses() calls a react-query hook internally, so it MUST be called here,
    // at the top level of the component — never inside a column's render function, which
    // runs during the parent's render where a hook call is illegal and would break the
    // hook order the moment the row count changed. The whole map is threaded into
    // buildB2BColumns, which picks the PRESORTED or STRIPS list per row.
    const orderStatuses = getOrderStatuses();

    const partner = searchParams.get(PARAM.partner) || "";
    const q = searchParams.get(PARAM.q) || "";
    const page = Math.max(Number(searchParams.get(PARAM.page)) || 1, 1);

    // A specific chip filters by that partnerId; "All" asks for every partnered order
    // via `hasPartner`. Both are server-side, so every row that comes back belongs on
    // this page and the pagination describes the partner orders rather than all orders.
    //
    // This replaced a client-side `.filter(row => row.partnerId)` over a page of ALL
    // orders, which meant "All" showed whatever few partner orders happened to land on
    // that page -- 2 rows out of a page of 20 -- while the pager still read
    // "Page 1 of 588" against 11,760 total. There are 1,565 partner orders.
    const ordersQuery = useOrdersList({
        partnerId: partner,
        hasPartner: !partner,
        search: q,
        page,
        size: DEFAULT_PAGE_SIZE,
    });

    const rows = ordersQuery.data?.orders ?? [];
    const pagination = ordersQuery.data?.pagination ?? { page, size: DEFAULT_PAGE_SIZE, total: 0 };

    const updateParams = (changes, { resetPage = true } = {}) => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => {
            if (value) next.set(key, value);
            else next.delete(key);
        });
        if (resetPage) next.delete(PARAM.page);
        setSearchParams(next, { replace: true });
    };

    const handleReasonChange = (row, reason) => {
        setSavingId(row.orderId);
        setSavingLabel("cancel comment");
        updateOrder.mutate(
            { orderId: row.orderId, changes: { cancelledReason: reason } },
            { onSettled: () => setSavingId(null) }
        );
    };

    const handleStatusChange = (row, status) => {
        // A no-op change still costs a round trip and would flash the row as saving.
        if (!status || status === row.status) return;
        setSavingId(row.orderId);
        setSavingLabel("status change");
        // ONLY `{ status }` — see the payload-discipline note in useUpdateOrder. The hook
        // invalidates ["ops-orders"] on settle, so the table re-reads what was stored
        // rather than trusting an optimistic local edit.
        updateOrder.mutate(
            { orderId: row.orderId, changes: { status } },
            { onSettled: () => setSavingId(null) }
        );
    };

    const handleEdit = (row) => {
        // KNOWN, ACCEPTED SEAM: this leaves the /v2 shell for the LEGACY order editor at
        // /order/edit/:id. There is no v2-native editor, and the legacy one is the single
        // implementation ops already uses — a second one would be the drift this console
        // exists to remove. Legacy also appends `?orderType=`, which is deliberately
        // omitted: edit-orders reads the type from the order it fetches by id
        // (src/pages/edit-orders/index.jsx:557), and existing call sites such as
        // src/pages/edit-quotations/index.jsx:673 already navigate here without it.
        navigate(`/order/edit/${row.orderId}`);
    };

    // Delete is reached only after RowActions' confirm(), and only on rows this page's
    // `canDeleteOrder` guard (no SUCCESS/PENDING payment) allowed a button for.
    const handleDelete = async (row) => {
        setSavingId(row.orderId);
        setDeleteError(null);
        let deleted = false;
        try {
            const result = await request({
                url: `/userOrder/admin/id/${row.orderId}`,
                method: "DELETE",
            });
            // Two separate failure modes, both of which RESOLVE rather than throw:
            // `request()` never rejects (see src/v2/hooks/request-error.js), and
            // pillup-api answers HTTP 200 with `success: false` for in-band errors. A
            // bare `await` with no check would treat both as a successful delete.
            if (isRequestError(result) || !result?.data?.success) {
                setDeleteError(result?.data?.error || "Please try again in a moment.");
            } else {
                deleted = true;
            }
        } catch (error) {
            // request() is not supposed to reject, but an async click handler that threw
            // would leave the row stuck disabled with nothing on screen explaining why.
            setDeleteError("Please try again in a moment.");
        } finally {
            setSavingId(null);
        }

        // Only after the server confirmed the delete. Refetching regardless would hide a
        // failure behind an unchanged-looking table.
        if (deleted) {
            // Drop the row from the selection too, or select-all would keep counting an
            // order that no longer exists.
            setSelectedIds((prev) => {
                const next = new Set(prev);
                next.delete(String(row.orderId));
                return next;
            });
            ordersQuery.refetch();
        }
    };

    // Selection is over the rows actually on screen — i.e. after the partner narrowing
    // below, not the raw page of results.
    const allSelected = rows.length > 0 && rows.every((row) => selectedIds.has(String(row.orderId)));

    const toggleRow = (id) =>
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });

    const toggleAll = () =>
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (allSelected) rows.forEach((row) => next.delete(String(row.orderId)));
            else rows.forEach((row) => next.add(String(row.orderId)));
            return next;
        });

    const columns = useMemo(
        () =>
            buildB2BColumns({
                savingId,
                onReasonChange: handleReasonChange,
                orderStatuses,
                onStatusChange: handleStatusChange,
                selectedIds,
                onToggleRow: toggleRow,
                onEdit: handleEdit,
                onDelete: handleDelete,
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [savingId, orderStatuses, selectedIds, rows]
    );

    const selectedRows = rows.filter((row) => selectedIds.has(String(row.orderId)));

    const handleDownloadExcel = () => {
        // Legacy exports the whole page because selection there drives other bulk
        // actions. Here selection has no other job, so it drives the export: tick some
        // rows to export those, tick none to export the page — which is legacy's
        // behaviour whenever nothing is selected. The count next to the button below
        // makes which of the two is about to happen visible before the click.
        const source = selectedRows.length > 0 ? selectedRows : rows;
        const { headers, data } = buildB2BExport(columns, source);
        DownLoadTable("B2B-Orders", "B2B-Orders-data", headers, data);
    };

    const partners = partnersQuery.data ?? [];

    return (
        <PageContainer fullBleed>
            <header>
                <h1 className="text-2xl font-bold text-black font-sans">B2B</h1>
                <p className="mt-1 max-w-[520px] text-sm font-sans text-gray-500">
                    Orders placed through a partner. Chips are built from the partner list, so a new partner appears
                    here as soon as it exists.
                </p>
            </header>

            <section
                aria-label="Filters"
                className="flex flex-col gap-4 my-2"
            >
                <div>
                    <p className="mb-1.5 text-sm font-bold font-sans text-black">B2B Partner</p>
                    <div className="flex flex-wrap gap-2">
                        <button
                            type="button"
                            onClick={() => updateParams({ [PARAM.partner]: "" })}
                            className={`rounded-[10px] px-2 py-1 text-[10px] font-semibold font-sans flex items-center gap-2 ${
                                !partner
                                    ? "bg-elm-500 text-white-50"
                                    : "bg-gray-200 text-[#333]"
                            }`}
                        >
                            All
                        </button>
                        {partners.map((entry) => (
                            <button
                                key={entry.id}
                                type="button"
                                onClick={() => updateParams({ [PARAM.partner]: entry.id })}
                                className={`rounded-[10px] px-2 py-1 text-[10px] font-semibold font-sans flex items-center gap-2 ${
                                    entry.id === partner
                                        ? "bg-elm-500 text-white-50"
                                        : "bg-gray-200 text-[#333]"
                                }`}
                            >
                                {entry.name}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="min-w-[220px] max-w-md">
                    <p className="mb-1 text-sm font-bold font-sans text-black">Search</p>
                    {/* Legacy's records banner is nothing more than this placeholder
                        (src/components/orderTable/orders.jsx:452). `pagination.total` is
                        the SERVER's count, which is the right number for this box even in
                        the "All partners" view where the visible rows are narrowed client
                        side below: the search runs server side, so what it searches over
                        is the server set. Falls back to the original prompt until a count
                        exists (first load, or an error). */}
                    <SearchInput
                        value={q}
                        onChange={(value) => updateParams({ [PARAM.q]: value })}
                        placeholder={pagination.total ? `${pagination.total} records...` : SEARCH_PLACEHOLDER}
                    />
                </div>
            </section>

            {updateOrder.isError && (
                <p className="text-sm font-sans text-[#dc143c]">
                    That {savingLabel} didn&apos;t save. The order is unchanged — try again.
                </p>
            )}

            {deleteError && (
                <p className="text-sm font-sans text-[#dc143c]">
                    That order wasn&apos;t deleted. {deleteError}
                </p>
            )}

            {ordersQuery.isLoading || ordersQuery.isIdle ? (
                <div
                    data-testid="ops-b2b-loading"
                    className="block w-full overflow-x-auto py-4 bg-[white] rounded-2xl shadow-soft-xl mt-4"
                >
                    <table className="w-full min-w-full border-collapse text-sm">
                        <tbody>
                            <TableSkeleton rows={DEFAULT_PAGE_SIZE} cols={8} />
                        </tbody>
                    </table>
                </div>
            ) : ordersQuery.isError ? (
                <div
                    data-testid="ops-b2b-error"
                    className="block py-4 bg-[white] rounded-2xl shadow-soft-xl mt-4"
                >
                    <EmptyState
                        title="Couldn't load partner orders"
                        hint="Something went wrong reaching the orders service. Try again in a moment."
                    />
                </div>
            ) : rows.length === 0 ? (
                <div className="block py-4 bg-[white] rounded-2xl shadow-soft-xl mt-4">
                    <EmptyState
                        title="No partner orders here"
                        hint="Try another partner, or clear the search."
                    />
                </div>
            ) : (
                <div className="flex flex-col gap-2">
                    <TableToolbar
                        allSelected={allSelected}
                        onToggleAll={toggleAll}
                        onDownloadExcel={handleDownloadExcel}
                    >
                        {selectedRows.length > 0 && (
                            <p className="text-sm font-sans text-gray-500">
                                Exporting {selectedRows.length} selected row
                                {selectedRows.length === 1 ? "" : "s"}
                            </p>
                        )}
                    </TableToolbar>
                    <DataTable columns={columns} rows={rows} />
                    <Pagination
                        page={pagination.page}
                        size={pagination.size}
                        total={pagination.total}
                        onChange={(nextPage) => updateParams({ [PARAM.page]: String(nextPage) }, { resetPage: false })}
                        // No custom label any more. It used to override the default
                        // because "All partners" narrowed the rows client-side while
                        // `total` still counted every order, so "Showing 1–20 of 11,742"
                        // would have been a figure the page couldn't back up. Now both
                        // views are server-filtered and `total` counts exactly what is
                        // being listed, so the default label is simply true.
                    />
                </div>
            )}

            {/* Outside the loading/empty/table branches on purpose: an ops user who has
                seen the design will go looking for these columns whether or not this
                page happens to have rows right now. Blank columns would imply the data
                exists and is missing; this says it is not recorded, and why. */}
            <details className="mt-4 text-sm font-sans text-gray-500">
                <summary className="cursor-pointer font-semibold">
                    {UNAVAILABLE_COLUMNS.length} columns from the design aren&apos;t shown
                </summary>
                <ul className="mt-2 flex flex-col gap-1 pl-4">
                    {UNAVAILABLE_COLUMNS.map((column) => (
                        <li key={column.label} className="list-disc">
                            <span className="font-semibold">{column.label}</span> — {column.reason}
                        </li>
                    ))}
                </ul>
            </details>
        </PageContainer>
    );
}

export default B2BPage;
